import React from 'react';
import { useNavigate } from 'react-router-dom';
import CustomerPage from '../../homeDashboard/templates/CustomerPage';
import data from "../../homeDashboard/static/data/CategoryData";
import { motion } from "framer-motion";
import { FaChevronDown, FaChevronRight, FaShoppingCart, FaTrash } from 'react-icons/fa';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import "@fortawesome/fontawesome-free/css/all.min.css";
import moment from 'moment-timezone';
import { initiatePayment } from '../../Services/ApiCallsServices/HomeDashboardApi';
import { useAuth } from '../../context/AuthContext';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import { useSelector, useDispatch } from 'react-redux';
import {
    addToCart,
    removeFromCart,
    setQuantity,
    toggleCategory,
    toggleSubcategory,
    setLoading
} from '../../Reducers/cartSlice';

function PackageDescription() {
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const {
        items: cart,
        quantities,
        expandedCategory,
        expandedSubcategory,
        loading
    } = useSelector(state => state.cart);

    const pickupAddress = useSelector((state) => state.location.pickupAddress);
    const dropoffAddress = useSelector((state) => state.location.dropoffAddress);
    const selectedDate = useSelector((state) => new Date(state.datetime.selectedDate));
    const selectedTime = useSelector((state) => state.datetime.selectedTime);

    const { auth } = useAuth();
    const email = auth.user?.email;

    const handleAddToCart = (product) => {
        dispatch(addToCart({
            name: product,
            quantity: quantities[product] || 1
        }));
        toast.success(`${product} x ${quantities[product] || 1} added to cart!`);
    };

    const handleRemoveProduct = (productName) => {
        dispatch(removeFromCart(productName));
        toast.warn("Removed from cart.");
    };

    const handleQuantityChange = (productName, value) => {
        dispatch(setQuantity({
            name: productName,
            value: parseInt(value) || 1
        }));
    };

    const handleContinue = async () => {
        if (cart.length === 0) {
            toast.error("Please add at least one product before continuing.");
            return;
        }

        dispatch(setLoading(true));

        try {
            const formData = new FormData();
            formData.append('description', "description");
            formData.append('email', email);

            // Use pickupAddress/dropoffAddress for string locations
            formData.append('pickupLocation', pickupAddress);
            formData.append('dropoffLocation', dropoffAddress);


            formData.append('selectedDate', moment(selectedDate).tz('Africa/Johannesburg').format("YYYY-MM-DD"));
            formData.append('selectedTime', selectedTime);

            cart.forEach(item => {
                formData.append('products[]', item.name);
                formData.append('quantities[]', item.quantity);
            });

            const createdOn = moment().tz('Africa/Johannesburg').format("YYYY-MM-DDTHH:mm:ssZ");
            formData.append('createdOn', createdOn);

            const response = await initiatePayment(formData);

            navigate('/confirm-trip', {
                state: {
                    pickupLocation: pickupAddress,
                    dropoffLocation: dropoffAddress,
                    distance: response.distance,
                    amount: response.amount,
                    reference: response.reference,
                    selectedDate,
                    selectedTime,
                    duration: response.duration
                }
            });

        } catch (error) {
            toast.error("Unable to connect to the server.");
            console.error("Payment initiation error:", error);
        } finally {
            dispatch(setLoading(false));
        }
    };

    return (
        <CustomerPage>
            <div className="min-h-screen bg-gray-50 p-4 md:p-8">
                <h1 className="text-3xl font-bold text-gray-800 mb-6 text-center">
                    Package <span className="text-yellow-500">Description</span>
                </h1>

                <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* Product Selection - Left Column */}
                    <div className="lg:col-span-2 space-y-4">
                        {data.categories.map(category => (
                            <div key={category.name} className="bg-white rounded-xl shadow-md overflow-hidden">
                                <button
                                    onClick={() => dispatch(toggleCategory(category.name))}
                                    className="w-full flex justify-between items-center p-4 hover:bg-gray-50 transition-colors"
                                >
                                    <h2 className="text-xl font-semibold text-gray-800">{category.name}</h2>
                                    {expandedCategory === category.name ? (
                                        <FaChevronDown className="text-yellow-500" />
                                    ) : (
                                        <FaChevronRight className="text-yellow-500" />
                                    )}
                                </button>

                                {expandedCategory === category.name && (
                                    <motion.div
                                        initial={{ opacity: 0 }}
                                        animate={{ opacity: 1 }}
                                        className="px-4 pb-4 space-y-4"
                                    >
                                        {category.subcategories.map(subcategory => (
                                            <div key={subcategory.name} className="bg-gray-50 rounded-lg p-3">
                                                <button
                                                    onClick={() => dispatch(toggleSubcategory(subcategory.name))}
                                                    className="w-full flex justify-between items-center"
                                                >
                                                    <h3 className="font-medium text-gray-700">{subcategory.name}</h3>
                                                    {expandedSubcategory === subcategory.name ? (
                                                        <FaChevronDown className="text-yellow-500 text-sm" />
                                                    ) : (
                                                        <FaChevronRight className="text-yellow-500 text-sm" />
                                                    )}
                                                </button>

                                                {expandedSubcategory === subcategory.name && (
                                                    <motion.ul
                                                        initial={{ opacity: 0 }}
                                                        animate={{ opacity: 1 }}
                                                        className="mt-2 space-y-2"
                                                    >
                                                        {subcategory.products.map(product => (
                                                            <li key={product} className="flex items-center justify-between p-2 bg-white rounded-md">
                                                                <span className="text-gray-800">{product}</span>
                                                                <div className="flex items-center space-x-2">
                                                                    <input
                                                                        type="number"
                                                                        min="1"
                                                                        value={quantities[product] || 1}
                                                                        onChange={(e) => handleQuantityChange(product, e.target.value)}
                                                                        className="w-16 px-2 py-1 border border-gray-300 rounded-md focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
                                                                    />
                                                                    <button
                                                                        onClick={() => handleAddToCart(product)}
                                                                        className="p-2 bg-yellow-500 text-white rounded-md hover:bg-yellow-600 transition-colors"
                                                                    >
                                                                        <FaShoppingCart />
                                                                    </button>
                                                                </div>
                                                            </li>
                                                        ))}
                                                    </motion.ul>
                                                )}
                                            </div>
                                        ))}
                                    </motion.div>
                                )}
                            </div>
                        ))}
                    </div>

                    {/* Cart Sidebar - Right Column */}
                    <div className="bg-white rounded-xl shadow-md p-6 h-fit sticky top-6">
                        <h2 className="text-xl font-semibold text-gray-800 mb-4 flex items-center">
                            <FaShoppingCart className="text-yellow-500 mr-2" />
                            Items in Cargo
                        </h2>

                        {cart.length === 0 ? (
                            <p className="text-gray-500 italic">Your cart is empty</p>
                        ) : (
                            <ul className="space-y-3 mb-6">
                                {cart.map(item => (
                                    <li key={item.name} className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                                        <span className="font-medium">
                                            {item.name} <span className="text-yellow-600">× {item.quantity}</span>
                                        </span>
                                        <button
                                            onClick={() => handleRemoveProduct(item.name)}
                                            className="text-red-500 hover:text-red-700 transition-colors"
                                        >
                                            <FaTrash />
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        )}

                        <button
                            onClick={handleContinue}
                            disabled={loading || cart.length === 0}
                            className={`w-full py-3 px-4 bg-yellow-500 hover:bg-yellow-600 text-white font-medium rounded-lg transition-colors flex items-center justify-center ${
                                loading || cart.length === 0 ? 'opacity-70 cursor-not-allowed' : ''
                            }`}
                        >
                            {loading ? (
                                <>
                                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                    Processing...
                                </>
                            ) : (
                                'Continue to Payment'
                            )}
                        </button>
                    </div>
                </div>
            </div>

            <Snackbar autoHideDuration={3000}>
                <Alert severity="success">Operation successful!</Alert>
            </Snackbar>
        </CustomerPage>
    );
}

export default PackageDescription;